import { useDrawerStore } from '../../store';
import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import FormDrawer from '../../components/ui/FormDrawer';
import ErrorModal from '../../components/ui/modal/ErrorModal';
import {
  useCreateData,
  useFetchData,
  useFetchFile,
  usePartnerStore,
  useUpdateData,
} from '../../hooks';
import { BaseModel, FormField, PageableResponse } from '../../types';
import AttachmentUpload from '../../components/AttachmentUpload';
import { Resource } from '../../lib/enums';
import dayjs from 'dayjs';
import { dateFormat } from '../../lib/utils';

/**
 * Supplier Type values from the ACC_MMD supplier form. Held as plain strings
 * (the column is a varchar) rather than a lookup table, because the four values
 * are fixed by the form and not maintained by users.
 */
const SUPPLIER_TYPES = [
  { id: 'Local (QC)', code: 'Local (QC)', description: 'Local (QC)' },
  { id: 'Local (STP)', code: 'Local (STP)', description: 'Local (STP)' },
  { id: 'Imported', code: 'Imported', description: 'Imported' },
  { id: 'Subcon', code: 'Subcon', description: 'Subcon' },
] as BaseModel[];

const PartnerForm = ({
  api,
  moduleName,
}: {
  api: string;
  moduleName: string;
}) => {
  // Buyers share this form; accreditation belongs to suppliers only.
  const isVendor = moduleName === 'Vendor';
  const drawerStore = useDrawerStore();
  const partnerStore = usePartnerStore();

  const [spinning, setSpinning] = useState<boolean>(false);
  const [isInfoModalOpen, setInfoModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>('');

  const { data: cityData, isLoading: isCitiesLoading } = useFetchData<
    PageableResponse<BaseModel>
  >('location/cities', {
    resource: Resource.MIN,
    search: '',
    length: '999999',
  });

  const { data: countryData, isLoading: isCountriesLoading } = useFetchData<
    PageableResponse<BaseModel>
  >('location/countries', {
    resource: Resource.MIN,
    search: '',
    length: '999999',
  });

  const { data: lineOfBusinessData, isLoading: isLineOfBusinessLoading } =
    useFetchData<PageableResponse<BaseModel>>('partner/line-of-bussiness', {
      resource: Resource.MIN,
      search: '',
      length: '999999',
    });

  // Accreditation documents. Only meaningful once the supplier exists -- there
  // is no record to attach to while creating one.
  const editingId = isVendor ? partnerStore.data?.id : undefined;
  const { data: attachments } = useFetchFile(
    `${api}/${editingId}/files`,
    `api/${api}`,
    undefined,
    { enabled: Boolean(editingId) },
  );

  const {
    mutate,
    isSuccess: isSuccessCreate,
    isError: isErrorCreate,
    error: errorCreate,
  } = useCreateData(api);
  const {
    mutate: mutateUpdate,
    isSuccess: isSuccessUpdate,
    isError: isErrorUpdate,
    error: errorUpdate,
  } = useUpdateData(api);

  const onFinish = (values: {
    city: string;
    country: string;
    lineOfBusiness: string;
  }) => {
    setSpinning(true);
    const city: BaseModel | undefined = cityData?.data?.find((obj) => {
      return obj.id == values.city;
    });
    const country: BaseModel | undefined = countryData?.data?.find((obj) => {
      return obj.id == values.country;
    });
    const lineOfBusiness: BaseModel | undefined =
      lineOfBusinessData?.data?.find((obj) => {
        return obj.id == values.lineOfBusiness;
      });
    const jsonData: any = {
      ...values,
      city: city,
      country: country,
      lineOfBusiness: lineOfBusiness,
    };

    if (isVendor) {
      // The picker hands back a dayjs object; the API expects YYYY-MM-DD,
      // same as the Purchase Order form does for its dates.
      jsonData.dateAccredited = (values as any).dateAccredited
        ? dayjs((values as any).dateAccredited).format(dateFormat)
        : null;
      jsonData.accredited = (values as any).accredited ?? false;
    }
    if (partnerStore.data !== null) {
      const updatedData = {
        ...partnerStore.data,
        ...jsonData,
      };
      mutateUpdate({ id: partnerStore.data.id, form: updatedData });
    } else {
      mutate(jsonData);
    }
  };

  useEffect(() => {
    if (isSuccessCreate || isSuccessUpdate || isErrorUpdate || isErrorCreate)
      setSpinning(false);

    if (isErrorUpdate) {
      setInfoModalOpen(true);
      setErrorMessage(errorUpdate?.response?.data.message);
    }
    if (isErrorCreate) {
      setInfoModalOpen(true);
      setErrorMessage(errorCreate?.response?.data.message);
    }
  }, [
    isSuccessCreate,
    isSuccessUpdate,
    isErrorUpdate,
    isErrorCreate,
    errorCreate,
  ]);

  const formFields: FormField[] = [
    {
      name: 'code',
      label: 'Code',
      rules: [{ required: true, message: 'Please enter Code' }],
      disabled: partnerStore.data != null,
      defaultValue: partnerStore.data?.code,
    },
    {
      name: 'description',
      label: 'Description',
      rules: [{ required: true, message: 'Please enter description' }],
      defaultValue: partnerStore.data?.description,
    },
    {
      name: 'address',
      label: 'Address',
      defaultValue: partnerStore.data?.address,
    },
    {
      name: 'city',
      label: 'City',
      isMultiple: false,
      type: 'select',
      allowClear: true,
      isLoading: isCitiesLoading,
      values: cityData?.data,
      defaultValue: partnerStore.data?.city?.id,
    },
    {
      name: 'country',
      label: 'Country',
      isMultiple: false,
      type: 'select',
      allowClear: true,
      isLoading: isCountriesLoading,
      values: countryData?.data,
      defaultValue: partnerStore.data?.country?.id,
    },
    {
      name: 'postalCode',
      label: 'Postal Code',
      defaultValue: partnerStore.data?.postalCode,
    },
    {
      name: 'email',
      label: 'Email Address',
      defaultValue: partnerStore.data?.email,
    },
    {
      name: 'phoneNo',
      label: 'Phone Number',
      defaultValue: partnerStore.data?.phoneNo,
    },
    {
      name: 'faxNo',
      label: 'Fax Number',
      defaultValue: partnerStore.data?.faxNo,
    },
    {
      name: 'website',
      label: 'Website',
      defaultValue: partnerStore.data?.website,
    },
    {
      name: 'agentName',
      label: 'Agent Name',
      defaultValue: partnerStore.data?.agentName,
    },
    {
      name: 'agentContact',
      label: 'Agent Contact',
      defaultValue: partnerStore.data?.agentContact,
    },
    {
      name: 'contactFinance',
      label: 'Contact Finance',
      defaultValue: partnerStore.data?.contactFinance,
    },
    {
      name: 'contactPerson',
      label: 'Contact Person',
      defaultValue: partnerStore.data?.contactPerson,
    },
    {
      name: 'contactShipping',
      label: 'Contact Shipping',
      defaultValue: partnerStore.data?.contactShipping,
    },
    {
      name: 'lineOfBusiness',
      label: 'Line of Business',
      isMultiple: false,
      type: 'select',
      allowClear: true,
      isLoading: isLineOfBusinessLoading,
      values: lineOfBusinessData?.data,
      defaultValue: partnerStore.data?.lineOfBusiness?.id,
    },
    // Supplier accreditation (ACC_MMD "New Supplier" form). This component is
    // shared with the Buyer screen, so these appear for suppliers only.
    ...(isVendor
      ? ([
          {
            name: 'supplierType',
            label: 'Supplier Type',
            type: 'radio-group-button',
            values: SUPPLIER_TYPES,
            defaultValue: partnerStore.data?.supplierType,
          },
          {
            name: 'materialSupplied',
            label: 'Material Supplied',
            defaultValue: partnerStore.data?.materialSupplied,
          },
          {
            name: 'yearEstablished',
            label: 'Year Established',
            type: 'numeric',
            defaultValue: partnerStore.data?.yearEstablished,
          },
          {
            name: 'ownershipType',
            label: 'Type of Ownership',
            defaultValue: partnerStore.data?.ownershipType,
          },
          {
            name: 'paymentTerms',
            label: 'Terms of Payment',
            defaultValue: partnerStore.data?.paymentTerms,
          },
          {
            name: 'accredited',
            label: 'Accredited',
            type: 'switch',
            defaultValue: partnerStore.data?.accredited ?? false,
          },
          {
            name: 'dateAccredited',
            label: 'Date Accredited',
            type: 'datepicker',
            defaultValue: partnerStore.data?.dateAccredited,
          },
        ] as FormField[])
      : []),
  ];

  return (
    <>
      <Spin spinning={spinning} fullscreen />
      <FormDrawer
        size={'large'}
        isOpen={drawerStore.isOpen}
        onClose={drawerStore.onClose}
        data={partnerStore.data}
        moduleName={moduleName}
        setData={partnerStore.setData}
        onFinish={onFinish}
        formFields={formFields}
      >
        {editingId && (
          <AttachmentUpload
            label={'Accreditation Documents'}
            attachments={attachments}
            api={api}
            recordId={editingId}
            tag={'accreditation'}
            canEdit
          />
        )}
      </FormDrawer>
      <ErrorModal
        visible={isInfoModalOpen}
        message={errorMessage}
        onClose={() => setInfoModalOpen(false)}
      />
    </>
  );
};

export default PartnerForm;
